/* =========================================================
   CATEGORY -> PRODUCT TYPE NESTING (Safe + AJAX-proof)
   - Parent facet: Category metafield (filter.p.m.custom.category)
   - Child facet: Product Type (filter.p.product_type)
   - Admin-driven via window.__FACET_GROUPS__
   - Safeguard: if no matching child LIs exist, DO NOT render expand UI
========================================================= */
(function CategoryProductTypeNesting() {
  const GROUPS = Array.isArray(window.__FACET_GROUPS__) ? window.__FACET_GROUPS__ : [];
  if (!GROUPS.length) return;

  // Adjust these if your filter param names differ
  const PARENT_FACET_PARAM = 'filter.p.m.custom.category';
  const CHILD_FACET_PARAM = 'filter.p.product_type';

  const ROOT_SELECTOR = 'facet-filters-form';
  const FACET_SELECTOR = '.js-filter';
  const LI_SELECTOR = 'li.facets__item';

  const MOVED_ATTR = 'data-facet-nest-moved';
  const PARENT_MARK_ATTR = 'data-facet-has-children';
  const NEST_ATTR = 'data-facet-nest';
  const CHILD_LIST_ATTR = 'data-facet-nest-children';

  const TOGGLE_CLASS = 'facet-group__toggle';
  const CHEVRON_CLASS = 'facet-group__chevron';
  const OPEN_CLASS = 'facet-nesting--open';

  const normalize = (s) => String(s || '').replace(/\s+/g, ' ').trim().toLowerCase();

  function debounce(fn, wait) {
    if (typeof window.debounce === 'function') return window.debounce(fn, wait);
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  }

  function findFacetByParam(root, paramName) {
    const facets = root.querySelectorAll(`${FACET_SELECTOR}[id^="Filter-"]`);
    // We can detect by looking at any checkbox name inside the facet list
    for (const facet of facets) {
      const anyInput = facet.querySelector(`input[type="checkbox"][name="${CSS.escape(paramName)}"]`);
      if (anyInput) return facet;
    }
    return null;
  }

  function getLabelElFromLi(li) {
    // Your theme uses <input class="product-option__input"> + <label for="...">
    return li ? li.querySelector('label') : null;
  }

  function getValueLabelFromLi(li) {
    const label = getLabelElFromLi(li);
    return label ? normalize(label.textContent) : '';
  }

  function findValueLiByLabel(facetEl, optionLabel) {
    if (!facetEl || !optionLabel) return null;
    const wanted = normalize(optionLabel);
    const lis = facetEl.querySelectorAll(LI_SELECTOR);
    for (const li of lis) {
      if (getValueLabelFromLi(li) === wanted) return li;
    }
    return null;
  }

  function ensureNestedContainer(parentLi) {
    let nest = parentLi.querySelector(`[${NEST_ATTR}]`);
    if (nest) return nest;

    nest = document.createElement('div');
    nest.setAttribute(NEST_ATTR, '');
    nest.innerHTML = `
      <div class="facet-group">
        <ul class="facets__list list-unstyled facet-group__list" ${CHILD_LIST_ATTR}></ul>
      </div>
    `;
    parentLi.appendChild(nest);
    return nest;
  }

  function ensureToggle(parentLi) {
    // Toggle sits next to the label, but parent checkbox remains native/selectable.
    let btn = parentLi.querySelector(`button.${TOGGLE_CLASS}`);
    if (btn) return btn;

    const labelEl = getLabelElFromLi(parentLi);
    if (!labelEl) return null;

    btn = document.createElement('button');
    btn.type = 'button';
    btn.className = TOGGLE_CLASS;
    btn.setAttribute('aria-expanded', 'false');
    btn.innerHTML = `<span class="${CHEVRON_CLASS}" aria-hidden="true">▾</span>`;

    // Put the chevron right after the label text
    labelEl.insertAdjacentElement('afterend', btn);

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const isOpen = parentLi.classList.toggle(OPEN_CLASS);
      btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

      const nest = parentLi.querySelector(`[${NEST_ATTR}]`);
      if (nest) nest.hidden = !isOpen;
    });

    return btn;
  }

  function cleanupParentUi(parentLi) {
    // Remove nested UI + toggle if present (for misconfigured / no children matches)
    parentLi.removeAttribute(PARENT_MARK_ATTR);
    parentLi.classList.remove(OPEN_CLASS);

    const toggle = parentLi.querySelector(`button.${TOGGLE_CLASS}`);
    if (toggle) toggle.remove();

    const nest = parentLi.querySelector(`[${NEST_ATTR}]`);
    if (nest) nest.remove();
  }

  function applyGroups(root) {
    const parentFacet = findFacetByParam(root, PARENT_FACET_PARAM);
    const childFacet = findFacetByParam(root, CHILD_FACET_PARAM);
    if (!parentFacet || !childFacet) return;

    GROUPS.forEach((group) => {
      if (!group || !group.parentLabel || !Array.isArray(group.childLabels)) return;

      const parentLi = findValueLiByLabel(parentFacet, group.parentLabel);
      if (!parentLi) return;

      // Collect existing child <li> elements that actually exist right now
      const childLis = [];
      group.childLabels.forEach((childLabel) => {
        const childLi = findValueLiByLabel(childFacet, childLabel);
        if (childLi) childLis.push(childLi);
      });

      // ✅ SAFEGUARD: if nothing matched, keep parent row native (no blank expand UI)
      if (!childLis.length) {
        cleanupParentUi(parentLi);
        return;
      }

      // Create UI (toggle + nest)
      parentLi.setAttribute(PARENT_MARK_ATTR, 'true');

      const nest = ensureNestedContainer(parentLi);
      const childList = nest.querySelector(`[${CHILD_LIST_ATTR}]`);
      if (!childList) return;

      const toggle = ensureToggle(parentLi);
      if (!toggle) return;

      // Default: collapsed
      const isOpen = parentLi.classList.contains(OPEN_CLASS);
      nest.hidden = !isOpen;
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

      // Move children into the nested list (idempotent by attr guard)
      childLis.forEach((childLi) => {
        if (childLi.hasAttribute(MOVED_ATTR)) return;
        childLi.setAttribute(MOVED_ATTR, group.parentLabel);
        childLi.classList.add('facet-group__item');
        childList.appendChild(childLi);
      });
    });
  }

  function initAll() {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      // Only run if facets exist in this root
      if (!root.querySelector(FACET_SELECTOR)) return;
      applyGroups(root);
    });
  }

  // Initial run
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-run on Shopify AJAX updates (facets re-render)
  const target = document.querySelector(ROOT_SELECTOR) || document.body;
  const rerun = debounce(initAll, 50);
  const observer = new MutationObserver(() => rerun());
  observer.observe(target, { childList: true, subtree: true });
})();

/* =========================================================
   FACET UI TWEAKS (Color: 2 columns + no show more)
                 (Size: button grid styling)
   Runs on load + on AJAX facet refresh (MutationObserver)
========================================================= */
(function FacetUiTweaks() {
  const ROOT_SELECTOR = 'facet-filters-form';
  const FACET_SELECTOR = '.js-filter';
  const LI_SELECTOR = 'li.facets__item';

  const normalize = (s) => String(s || '').toLowerCase();

  function debounce(fn, wait) {
    if (typeof window.debounce === 'function') return window.debounce(fn, wait);
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  }

  function markFacetTypes(root) {
    const facets = root.querySelectorAll(FACET_SELECTOR);

    facets.forEach((facet) => {
      facet.classList.remove('facet--color', 'facet--size');

      const inputs = facet.querySelectorAll('input[type="checkbox"][name]');
      let isColor = false;
      let isSize = false;

      inputs.forEach((inp) => {
        const name = normalize(inp.getAttribute('name'));
            // Variant options (filter.v.option.*)
            if (name.includes('filter.v.option')) {
            if (name.includes('option.color') || name.includes('option.colour')) isColor = true;
            if (name.includes('option.size')) isSize = true;
            }

            // Shopify taxonomy colour (filter.v.t.shopify.color-pattern / color)
            if (name.includes('filter.v.t.shopify.color')) {
            isColor = true;
            }
      });

      if (isColor) facet.classList.add('facet--color');
      if (isSize) facet.classList.add('facet--size');
    });
  }

  function applyColorRules(root) {
    root.querySelectorAll(`${FACET_SELECTOR}.facet--color`).forEach((facet) => {
      // Hide "show more" button (if theme adds it)
      const btn = facet.querySelector('button.show-more');
      if (btn) btn.style.display = 'none';

      // Ensure nothing is collapsed
      facet.querySelectorAll(`${LI_SELECTOR}.is-collapsed`).forEach((li) => li.classList.remove('is-collapsed'));
    });
  }

  function applySizeRules(root) {
    root.querySelectorAll(`${FACET_SELECTOR}.facet--size`).forEach((facet) => {
      // Hide "show more" for size too (usually size lists should be fully visible)
      const btn = facet.querySelector('button.show-more');
      if (btn) btn.style.display = 'none';

      // Ensure nothing is collapsed
      facet.querySelectorAll(`${LI_SELECTOR}.is-collapsed`).forEach((li) => li.classList.remove('is-collapsed'));

      // Add a helper class on each size item so CSS can style label like a button
      facet.querySelectorAll(LI_SELECTOR).forEach((li) => {
        li.classList.add('facet-size__item');
      });
    });
  }

  document.addEventListener("DOMContentLoaded", () => {
  document
    .querySelectorAll('.js-filter[data-filter-param="filter.p.product_type"]')
    .forEach((el) => {
      const details = el.closest("details.facets__accordion-section");
      if (details) details.style.display = "none";
    });
});

  function run() {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (!root.querySelector(FACET_SELECTOR)) return;

      markFacetTypes(root);
      applyColorRules(root);
      applySizeRules(root);
    });
  }

  // initial
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // re-run on AJAX changes
  const target = document.querySelector(ROOT_SELECTOR) || document.body;
  const rerun = debounce(run, 50);
  const observer = new MutationObserver(() => rerun());
  observer.observe(target, { childList: true, subtree: true });
})();